import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { BucketsAdjustmentTable } from "../app/components/domain/forecasting/BucketsAdjustmentTable";
import { BucketContentsPanel } from "../app/components/domain/forecasting/BucketContentsPanel";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AdjustmentWithMonths,
  AccessibleForecast,
  DriftSummary,
  ForecastCycle,
  ForecastSubmission,
} from "@shared-core/data/adapter";
import { useAuth, useCanManageTargets } from "../app/auth/hooks";
import {
  deadlineSuffix,
  deadlineUrgency,
  type DeadlineUrgency,
} from "@shared-core/domain/deadline";
import { toISODate, zoomToFit } from "@shared-core/domain/plan-range";
import { fmtK, fmtM } from "@shared-core/domain/format-money";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import {
  CURRENCY_VIEW_LABELS,
  SCENARIO_LABELS,
  type CurrencyView,
  type ScenarioKind,
} from "@shared-core/domain/plan-pickers";
import type * as API from "@shared-core/data/api-types";
import { FunnelBandsBar } from "../app/components/domain/pipeline/quote-funnel/FunnelBandsBar";
import { WinRateCurveChart } from "../app/components/domain/pipeline/quote-funnel/WinRateCurveChart";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../app/components/primitives/dropdown-menu";
import { Input } from "../app/components/primitives/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../app/components/primitives/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { DriftSincePublishPanel } from "../app/components/domain/forecasting/DriftSincePublishPanel";
import { ForecastDisclaimerBanner } from "../app/components/domain/forecasting/ForecastDisclaimerBanner";
import { AdjustmentGrid } from "../app/components/domain/forecasting/AdjustmentGrid";
import { CalibrationCard } from "../app/components/domain/forecasting/CalibrationCard";
import { usePlanCalibration } from "@shared-core/domain/use-plan-calibration";
import { LayeredTimelineChart } from "../app/components/domain/forecasting/LayeredTimelineChart";
import { MoversToReviewPanel } from "../app/components/domain/forecasting/MoversToReviewPanel";
import { WorkspaceNav } from "../app/components/domain/forecasting/WorkspaceNav";
import { useUrlPickerState } from "../app/components/domain/forecasting/use-url-picker-state";
import { paths } from "@shared-core/routing/routes";
import { currentTime } from "@shared-core/domain/current-time";

// ── Formatting ────────────────────────────────────────────────────

function pct(part: number, whole: number): string {
  if (whole === 0) return "—";
  return `${Math.round((part / whole) * 100)}%`;
}

// ── Open-quotes → pipeline funnel ─────────────────────────────────────
//
// Secondary supporting detail under each level's timeline chart, sourced
// from the composed /views/quote-funnel endpoint (open-quote book →
// win/age-weighted pipeline, REVENUE basis). The endpoint returns a
// company → rep → bucket tree; we index the rep and bucket nodes here so
// each drill level can read its own without walking the tree.

const ZERO_BANDS: API.QuoteFunnelBands = { hot: 0, warm: 0, cold: 0 };

/** The quote-funnel tree indexed by the plan-grid rep/bucket keys. */
interface QuoteFunnelTree {
  company: API.QuoteFunnelCompany | undefined;
  byRepId: Record<string, API.QuoteFunnelRep>;
  byBucketId: Record<string, API.QuoteFunnelBucket>;
}

// The funnel rep id and bucket id are the same COALESCE(pm.user_id, cp.id) /
// forecast_bucket_id keys the plan grid rows carry, so drill lookups match
// by rep.user_id / bucket.forecast_bucket_id (incl. the ownerless House
// Account row).
function buildQuoteFunnelTree(resp: API.QuoteFunnel): QuoteFunnelTree {
  const byRepId: Record<string, API.QuoteFunnelRep> = {};
  const byBucketId: Record<string, API.QuoteFunnelBucket> = {};
  for (const rep of resp.reps ?? []) {
    byRepId[rep.id] = rep;
    for (const bk of rep.buckets ?? []) byBucketId[bk.id] = bk;
  }
  return { company: resp.company, byRepId, byBucketId };
}

// Section header: static title left, the open→pipeline numbers right.
function FunnelHeader({
  openGbp,
  pipelineGbp,
}: {
  openGbp: number;
  pipelineGbp: number;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="text-sm font-semibold">
        Pipeline forecast (quotes and orders)
      </h3>
      <span className="text-xs text-muted-foreground">
        Open quotes{" "}
        <span className="tabular-nums text-foreground">{fmtM(openGbp)}</span> →
        pipeline{" "}
        <span className="tabular-nums text-foreground">
          {fmtM(pipelineGbp)}
        </span>
      </span>
    </div>
  );
}

// ── Picker bar ────────────────────────────────────────────────────

type WindowKind = "fy" | "rolling" | "custom";
type PeriodKind = "monthly" | "weekly" | "daily";

interface PickerBarProps {
  currency: CurrencyView;
  scenario: ScenarioKind;
  windowMode: WindowKind;
  period: PeriodKind;
  /** Custom-range from-date (ISO). Used only when windowMode === "custom". */
  customFrom: string;
  /** Custom-range to-date (ISO). Used only when windowMode === "custom". */
  customTo: string;
  targetPlanId: string;
  targetPlans: { id: string; label: string }[];
  onCurrency: (v: CurrencyView) => void;
  onScenario: (v: ScenarioKind) => void;
  onWindow: (v: WindowKind) => void;
  onPeriod: (v: PeriodKind) => void;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  onTargetPlan: (v: string) => void;
}

// The Cycle + Overlay pickers were removed in the plan/retro split:
// the plan page is always "now" (current open cycle + latest overlay),
// and historical replay moved to /plan/cycles/:cycleId/review. The
// status row above shows which open cycle is in scope.
function PickerBar(props: PickerBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface p-3">
      <Picker
        label="Currency"
        tooltip="How forecast values are denominated. Booked Contribution is the operational primary (post-margin GBP, dated by booking). Switch to Invoiced Contribution / Revenue variants for accounting-style views."
        value={props.currency}
        onChange={(v) => props.onCurrency(v as CurrencyView)}
        options={Object.entries(CURRENCY_VIEW_LABELS).map(([k, l]) => ({
          value: k,
          label: l,
        }))}
      />
      <Picker
        label="Scenario"
        tooltip="What-if multiplier on the algorithmic baseline only. Most Likely is the centre case; Good/Bad shift baseline up/down. Adjustments and actuals stay pinned across scenarios."
        value={props.scenario}
        onChange={(v) => props.onScenario(v as ScenarioKind)}
        options={Object.entries(SCENARIO_LABELS).map(([k, l]) => ({
          value: k,
          label: l,
        }))}
      />
      <Picker
        label="Targets"
        tooltip="The Target Plan version this view compares against. Versioned independently of the forecast — managers can revise targets without re-publishing forecasts."
        value={props.targetPlanId}
        onChange={props.onTargetPlan}
        options={props.targetPlans.map((p) => ({
          value: p.id,
          label: p.label,
        }))}
      />
      <Picker
        label="Window"
        tooltip="Which span the chart shows. FY = Apr-Mar of the fiscal year (monthly only). Rolling = today's month -3 → +8 (monthly only). Custom = pick from and to dates (any period)."
        value={props.windowMode}
        onChange={(v) => props.onWindow(v as WindowKind)}
        options={[
          { value: "fy", label: "FY 2026" },
          { value: "rolling", label: "Rolling 12" },
          { value: "custom", label: "Custom" },
        ]}
      />
      <Picker
        label="Period"
        tooltip="Bucket granularity. Monthly is the operational default; Weekly and Daily zoom in (capped at 120 buckets via the Custom range). Targets render on Monthly only — they're persisted per-month and don't distribute below."
        value={props.period}
        onChange={(v) => props.onPeriod(v as PeriodKind)}
        options={[
          { value: "monthly", label: "Monthly" },
          { value: "weekly", label: "Weekly" },
          { value: "daily", label: "Daily" },
        ]}
      />
      {props.windowMode === "custom" && (
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-1">
            <span className="text-muted-foreground">From:</span>
            <input
              type="date"
              value={props.customFrom}
              onChange={(e) => props.onCustomFrom(e.target.value)}
              className="h-8 px-2 border border-border rounded-md bg-background"
            />
          </label>
          <label className="flex items-center gap-1">
            <span className="text-muted-foreground">To:</span>
            <input
              type="date"
              value={props.customTo}
              onChange={(e) => props.onCustomTo(e.target.value)}
              className="h-8 px-2 border border-border rounded-md bg-background"
            />
          </label>
        </div>
      )}
    </div>
  );
}

function Picker({
  label,
  tooltip,
  value,
  onChange,
  options,
}: {
  label: string;
  tooltip?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; disabled?: boolean }[];
}) {
  const labelEl = (
    <span
      className={`text-xs font-medium text-muted-foreground ${
        tooltip
          ? "cursor-help border-b border-dotted border-muted-foreground/60"
          : ""
      }`}
    >
      {label}:
    </span>
  );
  return (
    <div className="flex items-center gap-2">
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{labelEl}</TooltipTrigger>
          <TooltipContent className="max-w-xs text-left normal-case">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        labelEl
      )}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-[180px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── Workspace nav ─────────────────────────────────────────────────

// WorkspaceNav lives in ../app/components/domain/forecasting/WorkspaceNav.tsx
// — imported above. Targets is manager-only, hidden otherwise.

// ── Company level ────────────────────────────────────────────────

interface CompanyLevelProps {
  reps: API.PlanGridRepRow[];
  companyFunnel?: API.QuoteFunnelCompany;
  monthLabels: string[];
  companyFyTarget: number;
  companyFyAdjusted: number;
  companyFyOutlook: number;
  companyFyActualsYtd: number;
  companyBaselineByMonth: number[];
  companyAdjustmentByMonth: number[];
  companyPipelineByMonth: number[];
  companyBookedPipelineByMonth: number[];
  companyActualsByMonth: number[];
  companyPriorYearByMonth: number[];
}

function CompanyLevel({
  reps,
  companyFunnel,
  monthLabels,
  companyFyTarget,
  companyFyAdjusted,
  companyFyOutlook,
  companyFyActualsYtd,
  companyBaselineByMonth,
  companyAdjustmentByMonth,
  companyPipelineByMonth,
  companyBookedPipelineByMonth,
  companyActualsByMonth,
  companyPriorYearByMonth,
}: CompanyLevelProps) {
  const companyCalibration = usePlanCalibration("company");
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold">Company FY26</h2>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <Stat
            label="Target"
            value={fmtM(companyFyTarget)}
            tooltip={TARGET_UNIT_TOOLTIP}
          />
          <Stat label="Actuals YTD" value={fmtK(companyFyActualsYtd)} />
          <Stat
            label="Forecast (remaining)"
            value={fmtM(companyFyAdjusted)}
            tooltip={
              "Forecast for the months still to come. The projection is zero " +
              "for elapsed months, so this is not a full-year figure."
            }
          />
          <Stat
            label="Full-year outlook"
            value={fmtM(companyFyOutlook)}
            tooltip={FCST_TOOLTIP}
          />
          <Stat
            label="vs Target"
            value={pct(companyFyOutlook, companyFyTarget)}
            tooltip={TARGET_UNIT_TOOLTIP}
          />
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <LayeredTimelineChart
          title="Blended forecast (baseline + pipeline + adjustments)"
          monthLabels={monthLabels}
          baseline={companyBaselineByMonth}
          adjustment={companyAdjustmentByMonth}
          pipeline={companyPipelineByMonth}
          bookedPipeline={companyBookedPipelineByMonth}
          actuals={companyActualsByMonth}
          priorYear={companyPriorYearByMonth}
          height="lg"
        />
        <CompanyFunnelBlock funnel={companyFunnel} />
      </Card>

      {/* Calibration card — relocated from the old /calibration tab.
          Headlines-only at company grain. */}
      {companyCalibration && <CalibrationCard data={companyCalibration} />}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-hover">
            <tr>
              <th className="text-left p-2 sticky left-0 bg-surface-hover">
                Rep
              </th>
              <th
                className="text-right p-2 font-medium"
                title={TARGET_UNIT_TOOLTIP}
              >
                Tgt
              </th>
              <th className="text-right p-2 font-medium" title={FCST_TOOLTIP}>
                Fcst
              </th>
              <th className="text-right p-2 font-medium" title={PY_TOOLTIP}>
                PY
              </th>
              {monthLabels.map((m) => (
                <th
                  key={m}
                  className="text-right p-2 font-medium"
                  title={MONTH_CELL_TOOLTIP}
                >
                  {m}
                </th>
              ))}
              <th className="p-2 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {reps.map((rep) => (
              <RepRow key={rep.user_id} rep={rep} />
            ))}
            <tr className="border-t-2 border-border bg-surface-hover/50 font-semibold">
              <td className="p-2 sticky left-0 bg-surface-hover/50">Company</td>
              <td className="text-right p-2">{fmtM(companyFyTarget)}</td>
              <td className="text-right p-2">{fmtM(companyFyOutlook)}</td>
              <td className="text-right p-2 text-muted-foreground">
                {fmtM(
                  reps.reduce(
                    (s, r) =>
                      s +
                      (r.prior_year_by_month ?? []).reduce((a, v) => a + v, 0),
                    0,
                  ),
                )}
              </td>
              {monthLabels.map((_, i) => {
                // Match the rep rows: elapsed months total actuals, remaining
                // months total forecast. Summing only the forecast band here
                // left every elapsed company cell at zero while the rep rows
                // above it showed actuals.
                const forecast = reps.reduce(
                  (s, r) => s + ((r.adjusted_forecast_by_month ?? [])[i] ?? 0),
                  0,
                );
                const actual = reps.reduce(
                  (s, r) => s + ((r.actuals_by_month ?? [])[i] ?? 0),
                  0,
                );
                const isActual = forecast === 0 && actual !== 0;
                const total = isActual ? actual : forecast;
                return (
                  <td
                    key={i}
                    className="text-right p-2 text-xs"
                    title={isActual ? "Actual" : undefined}
                  >
                    {total === 0 ? (
                      <span className="text-muted-foreground/40">··</span>
                    ) : isActual ? (
                      <span className="text-muted-foreground">
                        {fmtK(total)}
                      </span>
                    ) : (
                      fmtK(total)
                    )}
                  </td>
                );
              })}
              <td />
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// Company-level open-quotes → pipeline funnel, from the /views/quote-funnel
// company node. A secondary block under the timeline chart. Renders zeros
// when the funnel hasn't loaded yet.
function CompanyFunnelBlock({ funnel }: { funnel?: API.QuoteFunnelCompany }) {
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <FunnelHeader
        openGbp={funnel?.open_value ?? 0}
        pipelineGbp={funnel?.pipeline_value ?? 0}
      />
      <FunnelBandsBar bands={funnel?.bands ?? ZERO_BANDS} />
    </div>
  );
}

function RepRow({ rep }: { rep: API.PlanGridRepRow }) {
  const navigate = useNavigate();
  const fyPriorYear = (rep.prior_year_by_month ?? []).reduce(
    (s, v) => s + v,
    0,
  );
  const drill = () => navigate(paths.planRep({ repId: rep.user_id }));
  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={drill}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          drill();
        }
      }}
      className="border-t border-border cursor-pointer hover:bg-surface-hover/40 focus:outline-none focus:bg-surface-hover/40"
    >
      <td className="p-2 sticky left-0 bg-surface group-hover:bg-surface-hover/40">
        <span className="font-medium text-primary hover:underline">
          {rep.name}
        </span>
      </td>
      <td className="text-right p-2 tabular-nums text-muted-foreground">
        {fmtM(rep.fy.target)}
      </td>
      <td className="text-right p-2 tabular-nums font-medium">
        {fmtM(rep.fy.full_year_outlook)}
        {rep.adjustment_total_gbp !== 0 && (
          <span className="ml-1 text-xs text-amber-700">
            ({rep.adjustment_total_gbp > 0 ? "+" : ""}
            {fmtK(rep.adjustment_total_gbp)})
          </span>
        )}
      </td>
      <td className="text-right p-2 tabular-nums text-muted-foreground">
        {fmtM(fyPriorYear)}
      </td>
      {(rep.adjusted_forecast_by_month ?? []).map((v, i) => {
        // Two different quantities share this row: the forecast band is
        // structurally zero for elapsed months, so those cells fall back to
        // actuals. Render that difference instead of hiding it — a reader
        // otherwise assumes all 12 cells sum to Fcst, and they don't.
        // (Defensive fallback to the individual layers when the aggregate
        // field is sparse but its components are not.)
        const forecast =
          v !== 0
            ? v
            : (rep.baseline_by_month?.[i] ?? 0) +
              (rep.adjustment_by_month?.[i] ?? 0) +
              (rep.pipeline_by_month?.[i] ?? 0);
        const actual = rep.actuals_by_month?.[i] ?? 0;
        const isActual = forecast === 0 && actual !== 0;
        const total = isActual ? actual : forecast;
        return (
          <td
            key={i}
            className="text-right p-2 text-xs tabular-nums"
            title={isActual ? "Actual" : undefined}
          >
            {total === 0 ? (
              <span className="text-muted-foreground/40">··</span>
            ) : isActual ? (
              <span className="text-muted-foreground">{fmtK(total)}</span>
            ) : (
              fmtK(total)
            )}
          </td>
        );
      })}
      <td className="p-2 text-center text-muted-foreground">
        <ChevronRight className="h-4 w-4 inline" />
      </td>
    </tr>
  );
}

function Stat({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  /** Browser-native tooltip on the label. Use for unit / derivation notes. */
  tooltip?: string;
}) {
  return (
    <span>
      <span className="text-muted-foreground" title={tooltip}>
        {label}{" "}
      </span>
      <span className="font-semibold">{value}</span>
    </span>
  );
}

// Shared tooltip copy explaining the target unit + derivation convention.
// True regardless of which currency the picker is on: when on a
// contribution view the value IS the persisted target; when on a
// revenue view the value is derived per rep from their blended margin.
// The forecast band is structurally zero for elapsed months (the projection
// phases to 0 before as_at), so it is a REMAINING-months number mid-year.
// Only the full-year figure is period-comparable with the 12-month PY column.
const FCST_TOOLTIP =
  "Full-year outlook: actuals booked so far this financial year plus the " +
  "forecast for the remaining months. Comparable with PY, which is a full " +
  "12 months.";
const PY_TOOLTIP =
  "Prior year: the same 12 financial-year months, one year earlier, on the " +
  "same view and currency as the pickers above.";
const MONTH_CELL_TOOLTIP =
  "Elapsed months show actuals; remaining months show forecast.";

const TARGET_UNIT_TOOLTIP =
  "Targets are set in booked-contribution GBP. Revenue views show a derived equivalent " +
  "scaled by each rep's volume-weighted blended margin from current calibrations — " +
  "so the displayed number can shift as calibrations update.";

// ── Rep level ────────────────────────────────────────────────────

function RepLevel({
  rep,
  repFunnel,
  monthLabels,
  submissionId,
  adjustments,
  drift,
  onAdjustmentsChange,
}: {
  rep: API.PlanGridRepRow;
  repFunnel?: API.QuoteFunnelRep;
  monthLabels: string[];
  submissionId: string;
  adjustments: AdjustmentWithMonths[];
  drift: DriftSummary | null;
  onAdjustmentsChange: () => void;
}) {
  const navigate = useNavigate();
  const repCalibration = usePlanCalibration("rep", rep.user_id);
  const repAdjustments = useMemo(
    () =>
      adjustments.filter(
        (a) => a.scopeKind === "rep" && a.scopeRefId === rep.user_id,
      ),
    [adjustments, rep.user_id],
  );

  // Live adjustment layer = rep-scope adjustments + bucket-scope adjustments
  // for every bucket under this rep. Sums per month so the chart reflects
  // typed values immediately after each cell save.
  const liveRepAdjustmentByMonth = useMemo(() => {
    const fyStartYear = 2026;
    const out = Array(12).fill(0) as number[];
    const bucketIds = new Set(
      (rep.buckets ?? []).map((b) => b.forecast_bucket_id),
    );
    for (const adj of adjustments) {
      if (adj.forecastSubmissionId !== submissionId) continue;
      const inScope =
        (adj.scopeKind === "rep" && adj.scopeRefId === rep.user_id) ||
        (adj.scopeKind === "bucket" && bucketIds.has(adj.scopeRefId));
      if (!inScope) continue;
      for (const m of adj.months) {
        const idx = m.year === fyStartYear ? m.month - 4 : m.month + 8;
        if (idx >= 0 && idx < 12) out[idx] += m.bookedContributionAmtGbp;
      }
    }
    return out;
  }, [adjustments, submissionId, rep.user_id, rep.buckets]);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <Breadcrumb
          crumbs={[{ label: "Company", to: paths.plan() }, { label: rep.name }]}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <Stat
            label="Target"
            value={fmtM(rep.fy.target)}
            tooltip={TARGET_UNIT_TOOLTIP}
          />
          <Stat
            label="Baseline"
            value={fmtM(rep.fy.adjusted_forecast - rep.adjustment_total_gbp)}
          />
          <Stat
            label="Adjustments"
            value={`${rep.adjustment_total_gbp >= 0 ? "+" : ""}${fmtK(rep.adjustment_total_gbp)}`}
          />
          <Stat label="Adjusted" value={fmtM(rep.fy.adjusted_forecast)} />
          <Stat label="Actuals YTD" value={fmtK(rep.fy_actuals_ytd)} />
        </div>
      </Card>

      {drift && <DriftSincePublishPanel drift={drift} />}

      <div className="space-y-4">
        <Card className="p-4 space-y-4">
          <LayeredTimelineChart
            title="Blended forecast (baseline + pipeline + adjustments)"
            monthLabels={monthLabels}
            baseline={rep.baseline_by_month ?? []}
            adjustment={liveRepAdjustmentByMonth}
            pipeline={rep.pipeline_by_month ?? []}
            bookedPipeline={rep.booked_pipeline_by_month ?? []}
            actuals={rep.actuals_by_month ?? []}
            priorYear={rep.prior_year_by_month ?? []}
            height="md"
          />
          <RepFunnelBlock funnel={repFunnel} />
        </Card>

        {/* Calibration card — headlines-only at rep grain. */}
        {repCalibration && <CalibrationCard data={repCalibration} />}

        <Card className="p-4">
          <h3
            className="text-sm font-semibold mb-2"
            title={TARGET_UNIT_TOOLTIP}
          >
            Targets (per-month, manager-edited)
          </h3>
          <div
            className="grid grid-cols-13 gap-2 items-center"
            style={{ gridTemplateColumns: "repeat(12, 1fr) auto" }}
          >
            {(rep.target_by_month ?? []).map((t, i) => (
              <div key={i} className="text-center text-xs">
                <div className="text-muted-foreground">{monthLabels[i]}</div>
                <div className="font-medium">{fmtK(t)}</div>
              </div>
            ))}
            <div className="text-center text-xs pl-2 border-l border-border">
              <div className="text-muted-foreground">FY</div>
              <div className="font-semibold">{fmtK(rep.fy.target)}</div>
            </div>
          </div>
        </Card>

        <Card className="overflow-x-auto">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              Buckets{" "}
              <span className="font-normal text-muted-foreground">
                (this rep's customer/deal groups)
              </span>
            </h3>
            <span className="text-xs text-muted-foreground">
              Click a bucket name to see what's actually in it.
            </span>
          </div>
          <BucketsAdjustmentTable
            submissionId={submissionId}
            buckets={rep.buckets ?? []}
            monthLabels={monthLabels}
            adjustments={adjustments}
            onBucketClick={(bucketId) =>
              navigate(paths.planBucket({ repId: rep.user_id, bucketId }))
            }
            onAdjustmentsChange={onAdjustmentsChange}
          />
        </Card>

        <Card>
          <div className="p-3 border-b border-border">
            <h3 className="text-sm font-semibold">Rep-scope adjustments</h3>
          </div>
          <div className="p-3">
            <AdjustmentGrid
              submissionId={submissionId}
              scopeKind="rep"
              scopeRefId={rep.user_id}
              scopeLabel={rep.name}
              monthLabels={monthLabels}
              adjustments={repAdjustments}
              onChanged={onAdjustmentsChange}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

// Rep-level open-quotes → pipeline funnel, from the /views/quote-funnel rep
// node (keyed on rep.userId). Secondary block under the timeline chart.
// Renders zeros for a rep with no open quotes (no funnel node).
function RepFunnelBlock({ funnel }: { funnel?: API.QuoteFunnelRep }) {
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <FunnelHeader
        openGbp={funnel?.open_value ?? 0}
        pipelineGbp={funnel?.pipeline_value ?? 0}
      />
      <FunnelBandsBar bands={funnel?.bands ?? ZERO_BANDS} />
    </div>
  );
}

function Breadcrumb({ crumbs }: { crumbs: { label: string; to?: string }[] }) {
  return (
    <nav className="text-sm text-muted-foreground">
      {crumbs.map((c, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-1">›</span>}
          {c.to ? (
            <Link to={c.to} className="hover:underline">
              {c.label}
            </Link>
          ) : (
            <span className="text-foreground font-semibold">{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

// ── Bucket level ──────────────────────────────────────────────────

function BucketLevel({
  rep,
  bucket,
  bucketFunnel,
  monthLabels,
  submissionId,
  adjustments,
  onAdjustmentsChange,
}: {
  rep: API.PlanGridRepRow;
  bucket: API.PlanGridBucketRow;
  bucketFunnel?: API.QuoteFunnelBucket;
  monthLabels: string[];
  submissionId: string;
  adjustments: AdjustmentWithMonths[];
  onAdjustmentsChange: () => void;
}) {
  // One calibration payload per bucket: the calibration card renders it,
  // the open-quotes panel borrows the lead-time pair, and the AdjustmentGrid
  // inherits its margin/lead defaults from it — one source, so the inline
  // "inherited" values always match the card above.
  const bucketCalibration = usePlanCalibration(
    "bucket",
    bucket.forecast_bucket_id,
  );
  const bucketDefaults = useMemo(
    () => ({
      contributionPct: bucketCalibration?.contributionPct,
      bookingToInvoiceDays:
        bucketCalibration?.detail?.bookingToInvoiceMedianDays,
    }),
    [bucketCalibration],
  );

  const bucketAdjustments = useMemo(
    () =>
      adjustments.filter(
        (a) =>
          a.forecastSubmissionId === submissionId &&
          a.scopeKind === "bucket" &&
          a.scopeRefId === bucket.forecast_bucket_id,
      ),
    [adjustments, submissionId, bucket.forecast_bucket_id],
  );

  const liveAdjustmentTotalGbp = useMemo(
    () =>
      bucketAdjustments.reduce(
        (sum, adj) =>
          sum + adj.months.reduce((s, m) => s + m.bookedContributionAmtGbp, 0),
        0,
      ),
    [bucketAdjustments],
  );

  // FY: index 0 = April 2026; Jan–Mar belong to 2027.
  const liveAdjustmentByMonth = useMemo(() => {
    const fyStartYear = 2026;
    const out = Array(12).fill(0) as number[];
    for (const adj of bucketAdjustments) {
      for (const m of adj.months) {
        const idx = m.year === fyStartYear ? m.month - 4 : m.month + 8;
        if (idx >= 0 && idx < 12) out[idx] += m.bookedContributionAmtGbp;
      }
    }
    return out;
  }, [bucketAdjustments]);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <Breadcrumb
          crumbs={[
            { label: "Company", to: paths.plan() },
            { label: rep.name, to: paths.planRep({ repId: rep.user_id }) },
            { label: bucket.bucket_name },
          ]}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <Stat label="Baseline" value={fmtK(bucket.baseline_gbp)} />
          <Stat
            label="Adjustments"
            value={`${liveAdjustmentTotalGbp >= 0 ? "+" : ""}${fmtK(liveAdjustmentTotalGbp)}`}
          />
          <Stat
            label="Adjusted"
            value={fmtK(bucket.baseline_gbp + liveAdjustmentTotalGbp)}
          />
          <Stat label="Actuals YTD" value={fmtK(bucket.actuals_ytd_gbp)} />
        </div>
      </Card>

      <Card className="p-4">
        <LayeredTimelineChart
          title="Blended forecast (baseline + pipeline + adjustments)"
          monthLabels={monthLabels}
          baseline={bucket.baseline_by_month ?? []}
          adjustment={liveAdjustmentByMonth}
          pipeline={bucket.pipeline_by_month ?? []}
          bookedPipeline={bucket.booked_pipeline_by_month ?? []}
          actuals={bucket.actuals_by_month ?? []}
          priorYear={bucket.prior_year_by_month ?? []}
          height="lg"
        />
      </Card>

      {/* Adjustments sit directly under the outlook — they're the thing a
          rep acts on; calibration context reads below. */}
      <Card>
        <div className="p-3 border-b border-border">
          <h3 className="text-sm font-semibold">Adjustments</h3>
        </div>
        <div className="p-3">
          <AdjustmentGrid
            submissionId={submissionId}
            scopeKind="bucket"
            scopeRefId={bucket.forecast_bucket_id}
            scopeLabel={bucket.bucket_name}
            monthLabels={monthLabels}
            adjustments={bucketAdjustments}
            bucketDefaults={bucketDefaults}
            onChanged={onAdjustmentsChange}
          />
        </div>
      </Card>

      {/* Calibration card — bucket grain. The win-rate curve and the
          lead-time pair are NOT here: both live on the open-quotes panel
          below, which narrates the same estimate→booking→invoice
          conversion mechanics. */}
      {bucketCalibration && <CalibrationCard data={bucketCalibration} />}

      <BucketFunnelPanel
        funnel={bucketFunnel}
        leads={
          bucketCalibration?.detail && {
            estimateToBookingMedianDays:
              bucketCalibration.detail.estimateToBookingMedianDays,
            bookingToInvoiceMedianDays:
              bucketCalibration.detail.bookingToInvoiceMedianDays,
          }
        }
      />

      <BucketContentsPanel bucketId={bucket.forecast_bucket_id} />
    </div>
  );
}

// Bucket-level open-quotes → win-curve panel, from the /views/quote-funnel
// bucket node (keyed on bucket.bucketId). The full detail: the band split,
// the calibrated conversion lead times, and the calibrated win-curve with
// each open quote plotted on it — the page's single rendering of that curve
// (the calibration card deliberately doesn't repeat it). A bucket with no
// open quotes shows zeros, not the chart.
function BucketFunnelPanel({
  funnel,
  leads,
}: {
  funnel?: API.QuoteFunnelBucket;
  /** Calibrated conversion lead times — same source as the calibration
   *  card; shown here because they describe this panel's funnel. */
  leads?: {
    estimateToBookingMedianDays: number;
    bookingToInvoiceMedianDays: number;
  };
}) {
  return (
    <Card className="p-4 space-y-3">
      <FunnelHeader
        openGbp={funnel?.open_value ?? 0}
        pipelineGbp={funnel?.pipeline_value ?? 0}
      />
      {/* All-zero bands render as overlapping labels — skip the bar when
          the bucket has no open book (the copy below says why). */}
      {funnel && funnel.open_value > 0 && (
        <FunnelBandsBar bands={funnel.bands ?? ZERO_BANDS} />
      )}
      {leads && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <Stat
            label="Est → booking lead"
            value={`${leads.estimateToBookingMedianDays}wd`}
            tooltip="Median working days from estimate raised to booked order, derived from the win-rate curve."
          />
          <Stat
            label="Booked → invoice lead"
            value={`${leads.bookingToInvoiceMedianDays}wd`}
            tooltip="Median working days from booked order to invoice — lag-shifts booked-view projections."
          />
        </div>
      )}
      {funnel && (funnel.quotes ?? []).length > 0 ? (
        <WinRateCurveChart bucket={funnel} />
      ) : (
        <p className="text-xs text-muted-foreground">
          No open quotes in this bucket.
        </p>
      )}
    </Card>
  );
}

// ── Cycle banner ──────────────────────────────────────────────────

// ── Cycle status row + Manage menu (admin/manager affordances) ────
//
// Replaces the previous violet banner. The status row sits beneath the
// PickerBar as compact chrome — same layer of UI as the dropdowns
// above. Manage is a dropdown menu of one-shot actions (Edit / Cancel
// / Close); each click triggers the existing inline form below the
// PickerBar.
//
// Deadline styling escalates with proximity per shared deadline.ts:
//   comfortable (>7d) → muted, approaching (≤7d) → amber-600,
//   due-today → amber-700 + AlertTriangle, overdue → destructive +
//   AlertTriangle. Matches TicketCard / OpportunityCard / comms timeline
//   conventions in the rest of the repo.

function urgencyClasses(u: DeadlineUrgency): string {
  switch (u) {
    case "overdue":
      return "text-destructive font-medium";
    case "due-today":
      return "text-amber-700 font-medium";
    case "approaching":
      return "text-amber-600";
    case "comfortable":
    default:
      return "text-muted-foreground";
  }
}

function CycleDeadlineChip({ dueDate }: { dueDate: string }) {
  const urgency = deadlineUrgency(dueDate);
  const suffix = deadlineSuffix(dueDate);
  const showIcon = urgency === "overdue" || urgency === "due-today";
  return (
    <span
      className={`inline-flex items-center gap-1 ${urgencyClasses(urgency)}`}
    >
      {showIcon && <AlertTriangle className="h-3 w-3" />}
      {urgency === "overdue" || urgency === "due-today" ? (
        <span>{suffix}</span>
      ) : (
        <span>
          due {dueDate}{" "}
          <span className="text-muted-foreground">({suffix})</span>
        </span>
      )}
    </span>
  );
}

function CycleStatusRow({
  openCycle,
  isManager,
  automationNext,
  onOpenNew,
  onClose,
  onEdit,
  onCancel,
}: {
  openCycle: ForecastCycle | null;
  isManager: boolean;
  automationNext: string | null;
  onOpenNew: () => void;
  onClose: () => void;
  onEdit: () => void;
  onCancel: () => void;
}) {
  // Empty state — no cycle open. Managers get a single primary action;
  // reps see nothing (the next auto-open will land at the next tick).
  if (!openCycle) {
    if (!isManager) return null;
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <span>No forecast review cycle is open.</span>
        <Button size="sm" onClick={onOpenNew}>
          Open new cycle
        </Button>
      </div>
    );
  }

  const c = openCycle;
  const label = c.label || "Current cycle";

  return (
    <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">open</span>
      {c.dueDate ? (
        <>
          <span className="text-muted-foreground">·</span>
          <CycleDeadlineChip dueDate={c.dueDate} />
        </>
      ) : null}
      {automationNext ? (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">
            next cycle opens {automationNext}
          </span>
        </>
      ) : null}
      {c.notes ? (
        <span className="text-xs italic text-muted-foreground">{c.notes}</span>
      ) : null}

      <div className="ml-auto flex items-center gap-2">
        {isManager && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                Manage <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[10rem]">
              <DropdownMenuItem onSelect={onEdit}>Edit cycle</DropdownMenuItem>
              <DropdownMenuItem onSelect={onCancel}>
                Cancel cycle
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onClose}>
                Close cycle
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

// Rep personal-status row. Only rendered when the viewer IS the rep
// being viewed (gated by caller). Shows submit/reopen action inline
// next to the cycle status row instead of as a separate green banner.
function RepStatusRow({
  submission,
  busy,
  onSubmit,
  onReopen,
}: {
  submission: { id: string; submittedAt: string | null };
  busy: boolean;
  onSubmit: () => void;
  onReopen: () => void;
}) {
  if (submission.submittedAt) {
    const when = formatDateTimeOrDash(submission.submittedAt);
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <span>
          <span className="text-emerald-700">✓</span> You submitted {when}
        </span>
        <span className="ml-auto">
          <Button size="sm" variant="ghost" onClick={onReopen} disabled={busy}>
            {busy ? "…" : "Reopen"}
          </Button>
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span className="text-muted-foreground">
        Your review for this cycle is not yet submitted
      </span>
      <span className="ml-auto">
        <Button size="sm" onClick={onSubmit} disabled={busy}>
          {busy ? "Submitting…" : "Submit ▸"}
        </Button>
      </span>
    </div>
  );
}

// ── Page entry ────────────────────────────────────────────────────

export function PlanPage() {
  const { repId, bucketId } = useParams<{
    repId?: string;
    bucketId?: string;
  }>();
  const adapter = useDataAdapter();
  const isManager = useCanManageTargets();
  const { user: currentUser } = useAuth();
  // Personal rep-status row only renders when the viewer IS the viewed
  // rep. Managers drilled into a rep see info but no action prompt.
  const viewerIsViewedRep = Boolean(
    repId && currentUser?.id && currentUser.id === repId,
  );
  // Picker state lives in the URL so reload / share-by-link works.
  // Defaults match the legacy in-memory defaults; we clear the param
  // when the value equals the default so common URLs stay tidy.
  const [currencyRaw, setCurrency] = useUrlPickerState(
    "currency",
    "booked_contribution",
  );
  const currency = currencyRaw as CurrencyView;
  const [scenarioRaw, setScenario] = useUrlPickerState(
    "scenario",
    "most_likely",
  );
  const scenario = scenarioRaw as ScenarioKind;
  const [windowModeRaw, setWindowMode] = useUrlPickerState("window", "fy");
  const windowMode = windowModeRaw as WindowKind;
  const [periodRaw, setPeriod] = useUrlPickerState("period", "monthly");
  const period = periodRaw as PeriodKind;
  // Custom-range from/to dates. Seeded from FY26 so switching to
  // Custom doesn't briefly render a 0-bucket grid.
  const [customFrom, setCustomFrom] = useUrlPickerState("from", "2026-04-01");
  const [customTo, setCustomTo] = useUrlPickerState("to", "2027-03-31");

  // When period switches away from monthly, FY / Rolling stop making
  // sense (they're monthly-aggregated 12-cell presets). Auto-flip
  // Window to Custom in that case.
  useEffect(() => {
    if (period !== "monthly" && windowMode !== "custom") {
      setWindowMode("custom");
    }
  }, [period, windowMode, setWindowMode]);
  const [targetPlanId, setTargetPlanId] = useState<string>("");
  const [targetPlans, setTargetPlans] = useState<
    { id: string; label: string }[]
  >([]);
  // The composed plan-grid query is expensive, so it must fetch only
  // once on load — not once with an empty target plan and again when the
  // target plan resolves. Gate the grid fetch on the target plans having
  // loaded so it fires a single time with the resolved targetPlanId.
  const [targetPlansLoaded, setTargetPlansLoaded] = useState(false);

  // Forecast + submissions wiring. Company, rep, and bucket levels all load
  // from the composed /views/plan-grid endpoint (per-bucket monthlies and lock
  // state land on the response via Pass 7b/8b), and the open-quotes funnel from
  // /views/quote-funnel.
  const [forecast, setForecast] = useState<AccessibleForecast | null>(null);
  const [submissions, setSubmissions] = useState<ForecastSubmission[]>([]);
  const [submissionId, setSubmissionId] = useState<string>("");
  const [adjustments, setAdjustments] = useState<AdjustmentWithMonths[]>([]);
  const [drift, setDrift] = useState<DriftSummary | null>(null);
  const [planGrid, setPlanGrid] = useState<API.PlanGrid | null>(null);
  const [quoteFunnel, setQuoteFunnel] = useState<QuoteFunnelTree | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadAdjustmentsToken, setReloadAdjustmentsToken] = useState(0);

  // Forecast review cycles. Plan page only ever shows the currently
  // open cycle — historical replay moved to /plan/cycles/:cycleId/review.
  // Still load the list so the cycle-management actions (cancel ->
  // reopen previous, etc.) have the data they need.
  const [cycles, setCycles] = useState<ForecastCycle[]>([]);
  // `cycles` starts empty, which is indistinguishable from "loaded, and there
  // is no open cycle" — and that ambiguity used to resolve submissionId twice
  // on load (once against the empty list, again once the cycles arrived),
  // re-fetching adjustments + drift each time. Same gate-on-loaded shape as
  // `targetPlansLoaded` above, for the same reason.
  const [cyclesLoaded, setCyclesLoaded] = useState(false);
  // The user's accessible forecasts, fetched once. Both the bootstrap effect
  // and the rep-drill effect below need this list; the drill effect used to
  // re-request it, which is a wasted round trip for a value that cannot have
  // changed (its only input is the adapter).
  const [accessibleForecasts, setAccessibleForecasts] = useState<
    AccessibleForecast[]
  >([]);

  // Load the user's accessible forecasts and bootstrap to the latest submission.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const accessible = await adapter.listForecasts();
        if (cancelled || accessible.length === 0) return;
        setAccessibleForecasts(accessible);
        const f = accessible[0];
        const subs = await adapter.listForecastSubmissionsForForecast(f.id);
        if (cancelled) return;
        setForecast(f);
        setSubmissions(subs);
        // submissionId is deliberately NOT set here. The cycle-matching
        // effect below owns it and carries the same precedence as a
        // fallback, so setting an optimistic value here only produced a
        // different id that that effect then overrode — two adjustments +
        // drift fetches for one page load.
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  // Load the composed open-quotes → weighted-pipeline funnel tree once and
  // index it by the plan-grid rep/bucket keys so each drill level reads its
  // node. Portfolio-scoped server-side. Non-blocking: a failure just leaves
  // the funnel blocks reading zeros rather than dead-ending the page.
  useEffect(() => {
    let cancelled = false;
    // Follow the currency picker so the funnel reads in the same currency as the
    // 12-month chart beside it (the CurrencyView is view_currency, e.g.
    // "booked_contribution" — the funnel only needs the currency half).
    const currencyKind = currency.endsWith("revenue")
      ? "revenue"
      : "contribution";
    adapter
      .getQuoteFunnel(currencyKind)
      .then((resp) => {
        if (!cancelled) setQuoteFunnel(buildQuoteFunnelTree(resp));
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn("Failed to load quote funnel:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, currency]);

  // Load forecast cycles for management actions (cancel-reopen-previous,
  // cycle metadata edit, etc.). The plan page itself doesn't pick from
  // this list — it always operates on the single open cycle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await adapter.listForecastCycles();
        if (!cancelled) setCycles(list);
      } catch (err) {
        if (!cancelled) {
          console.warn("Failed to load forecast cycles:", err);
        }
      } finally {
        // Loaded-ness is flagged on the failure path too: a cycles fetch that
        // errors must still let the submission resolve through its no-open-cycle
        // fallback, rather than leaving the page permanently without one.
        if (!cancelled) setCyclesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  // When the user drills into a rep, switch the active forecast stream
  // (and reload its submissions) so the per-rep cycle wiring below can
  // find the cycle's submission for THAT rep — not the company bootstrap
  // forecast picked at first load.
  useEffect(() => {
    if (!repId || !planGrid) return;
    const repRow = planGrid.reps.find((r) => r.user_id === repId);
    const repForecastId = repRow?.forecast_id;
    if (!repForecastId) return;
    if (forecast?.id === repForecastId) return;
    const match = accessibleForecasts.find((f) => f.id === repForecastId);
    if (!match) return;
    let cancelled = false;
    (async () => {
      try {
        const subs = await adapter.listForecastSubmissionsForForecast(match.id);
        if (cancelled) return;
        setForecast(match);
        setSubmissions(subs);
      } catch (err) {
        if (!cancelled) {
          // Soft fail: keep showing whatever submissions we already had.

          console.warn("Failed to load rep's forecast stream:", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repId, planGrid, forecast, accessibleForecasts, adapter]);

  // Match the submissionId to (open cycle, forecast). Plan page always
  // operates on the open cycle's draft for the active forecast stream
  // — no cycle picker so no "which cycle?" branching here. Fallback to
  // rolling live submission when there's no open cycle (rare interregnum
  // between auto-tick fires).
  useEffect(() => {
    if (!forecast || submissions.length === 0) return;
    // Wait for the cycles to actually land. Acting on the initial empty list
    // resolves through the no-open-cycle fallback, then re-resolves to the
    // cycle's submission moments later — and each change re-fetches
    // adjustments + drift.
    if (!cyclesLoaded) return;
    const openCycleId = cycles.find((c) => c.closedAt === null)?.id ?? null;
    const target = openCycleId
      ? submissions.find(
          (s) => s.cycleId === openCycleId && s.forecastId === forecast.id,
        )
      : submissions.find((s) => s.status === "live" && !s.cycleId);
    // Fallback chain, previously duplicated in the bootstrap effect: prefer
    // live, then draft, then any. Live submissions are the company-wide source
    // of truth; drafts may only cover the portfolio of the rep who started
    // them, and defaulting to one makes /plan look empty for everyone else.
    // Kept here so this effect is the single owner of submissionId.
    const resolved =
      target ??
      submissions.find((s) => s.status === "live") ??
      submissions.find((s) => s.status === "draft") ??
      submissions[0];
    if (resolved && resolved.id !== submissionId) {
      setSubmissionId(resolved.id);
    }
  }, [cycles, cyclesLoaded, forecast, submissions, submissionId]);

  // Reload adjustments + drift whenever the selected submission changes.
  useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    (async () => {
      try {
        const [adj, d] = await Promise.all([
          adapter.listAdjustmentsForSubmission(submissionId),
          adapter.getDriftSincePublish(submissionId),
        ]);
        if (cancelled) return;
        setAdjustments(adj);
        setDrift(d);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, submissionId, reloadAdjustmentsToken]);

  // Load the composed plan grid for the company-level rollup.
  // Picker selections (currency / scenario / submission) flow into the
  // query so changes re-fetch with new SQL projections.
  useEffect(() => {
    // Wait for target plans to resolve so the grid fetches once with the
    // resolved targetPlanId rather than firing a second expensive query.
    if (!targetPlansLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const [view, currencyKind] = currency.split("_") as [
          "booked" | "invoiced",
          "contribution" | "revenue",
        ];
        // Frontend uses short scenario keys ("good"/"bad"), backend uses
        // "good_case"/"bad_case". Translate at the call site.
        const scenarioApi =
          scenario === "good"
            ? "good_case"
            : scenario === "bad"
              ? "bad_case"
              : "most_likely";
        // plan-grid composes the rollup across every live submission;
        // we never pin to a single submission here, otherwise the rep
        // sidebar would collapse to whichever rep owns that one
        // submission. The Forecast picker still drives adjustments +
        // drift via separate per-submission calls — those don't go
        // through plan-grid.
        // Apply zoom-to-fit on Custom + non-monthly ranges so we
        // never ask the backend for a chart with > 120 buckets.
        const effective =
          windowMode === "custom"
            ? zoomToFit({ from: customFrom, to: customTo }, period, {
                anchor: toISODate(currentTime()),
              })
            : null;
        const grid = await adapter.getPlanGrid(2026, {
          view,
          currency: currencyKind,
          scenario: scenarioApi,
          window: windowMode,
          period,
          rangeStart: effective?.from,
          rangeEnd: effective?.to,
          targetPlanId: targetPlanId || undefined,
        });
        if (!cancelled) setPlanGrid(grid);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    adapter,
    currency,
    scenario,
    targetPlanId,
    targetPlansLoaded,
    windowMode,
    period,
    customFrom,
    customTo,
  ]);

  // Load the available target plans for the FY so the Targets picker
  // shows real revisions rather than hardcoded mock IDs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await adapter.listTargetPlans(2026);
        if (cancelled) return;
        const plans = (list.plans ?? []).map((p) => ({
          id: p.id,
          label: `FY${String(p.fiscal_year).slice(-2)} v${p.version}${
            p.status === "published" ? "" : " (draft)"
          }`,
        }));
        setTargetPlans(plans);
        const live = (list.plans ?? []).find((p) => p.status === "published");
        if (live && !targetPlanId) setTargetPlanId(live.id);
        // Unblock the grid fetch now the target plan is resolved.
        setTargetPlansLoaded(true);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
          // Still let the grid load (backend defaults to the latest
          // published plan) even if the picker list failed to resolve.
          setTargetPlansLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, targetPlanId]);

  // Company, rep, and bucket views all render from the live /views/plan-grid
  // endpoint. Sort and zero-filter happen server-side; per-rep and per-bucket
  // monthly arrays (baseline / pipeline / actuals / prior-year) land on the
  // response via Pass 7b/8b, so drilling in shows real data all the way down.
  // A repId or bucketId the live response doesn't carry (a stale deep-link)
  // resolves to undefined and falls through to the honest "not found" cards
  // below — we never fabricate rep data.
  const liveReps = planGrid?.reps ?? [];
  const rep = repId ? liveReps.find((r) => r.user_id === repId) : undefined;
  const bucket =
    rep && bucketId
      ? (rep.buckets ?? []).find((b) => b.forecast_bucket_id === bucketId)
      : undefined;

  const reloadAdjustments = () => setReloadAdjustmentsToken((t) => t + 1);

  const currentSubmission = useMemo(
    () => submissions.find((s) => s.id === submissionId) ?? null,
    [submissions, submissionId],
  );
  // Per-stream draft/publish workflow removed in the cycles redesign:
  // cycles now drive draft creation (via open_cycle → create_draft_submission)
  // and publishing (via close_cycle → publish_submission). The legacy
  // banners that exposed these to reps individually have been deleted.
  // The underlying API endpoints remain — they're the substrate cycles
  // call into — and are reachable via the cycle open/close actions
  // below.

  // Cycle open/close UX. Both manager-only. Inline confirm forms (no
  // dialog primitives) — same pattern as the publish notes form above.
  const openCycle = useMemo(
    () => cycles.find((c) => c.closedAt === null) ?? null,
    [cycles],
  );
  const [openCycleFormOpen, setOpenCycleFormOpen] = useState(false);
  const [openCycleAsOf, setOpenCycleAsOf] = useState(
    currentTime().toISOString().slice(0, 10),
  );
  const [openCycleDueDate, setOpenCycleDueDate] = useState("");
  const [openCycleLabel, setOpenCycleLabel] = useState("");
  const [openCycleBusy, setOpenCycleBusy] = useState(false);
  const handleOpenCycle = async () => {
    setOpenCycleBusy(true);
    try {
      await adapter.openForecastCycle({
        asOfDate: openCycleAsOf,
        dueDate: openCycleDueDate || null,
        label: openCycleLabel,
      });
      const list = await adapter.listForecastCycles();
      setCycles(list);
      setOpenCycleFormOpen(false);
      setOpenCycleLabel("");
      setOpenCycleDueDate("");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpenCycleBusy(false);
    }
  };

  const [closeCycleFormOpen, setCloseCycleFormOpen] = useState(false);
  const [closeCycleNotes, setCloseCycleNotes] = useState("");
  const [closeCycleBusy, setCloseCycleBusy] = useState(false);
  const handleCloseCycle = async () => {
    if (!openCycle) return;
    setCloseCycleBusy(true);
    try {
      await adapter.closeForecastCycle(openCycle.id, {
        notes: closeCycleNotes,
      });
      const list = await adapter.listForecastCycles();
      setCycles(list);
      // No auto-open-next under the cycles-automation model — the
      // scheduled cycle-tick job opens the next cycle on cadence.
      setCloseCycleFormOpen(false);
      setCloseCycleNotes("");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setCloseCycleBusy(false);
    }
  };

  // Edit cycle metadata (in-place amend of the open cycle).
  const [editCycleFormOpen, setEditCycleFormOpen] = useState(false);
  const [editCycleLabel, setEditCycleLabel] = useState("");
  const [editCycleDueDate, setEditCycleDueDate] = useState("");
  const [editCycleNotes, setEditCycleNotes] = useState("");
  const [editCycleBusy, setEditCycleBusy] = useState(false);
  const openEditCycleForm = () => {
    if (!openCycle) return;
    setEditCycleLabel(openCycle.label);
    setEditCycleDueDate(openCycle.dueDate ?? "");
    setEditCycleNotes(openCycle.notes);
    setEditCycleFormOpen(true);
  };
  const handleEditCycle = async () => {
    if (!openCycle) return;
    setEditCycleBusy(true);
    try {
      await adapter.editForecastCycle(openCycle.id, {
        label: editCycleLabel,
        dueDate: editCycleDueDate || null,
        notes: editCycleNotes,
      });
      const list = await adapter.listForecastCycles();
      setCycles(list);
      setEditCycleFormOpen(false);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setEditCycleBusy(false);
    }
  };

  // Cancel cycle (full undo — reopens the previous cycle).
  const [cancelCycleFormOpen, setCancelCycleFormOpen] = useState(false);
  const [cancelCyclePreview, setCancelCyclePreview] = useState<Awaited<
    ReturnType<typeof adapter.getCancelCyclePreview>
  > | null>(null);
  const [cancelCycleBusy, setCancelCycleBusy] = useState(false);
  const openCancelCycleForm = async () => {
    if (!openCycle) return;
    setCancelCycleBusy(true);
    try {
      const preview = await adapter.getCancelCyclePreview(openCycle.id);
      setCancelCyclePreview(preview);
      setCancelCycleFormOpen(true);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setCancelCycleBusy(false);
    }
  };
  const handleCancelCycle = async () => {
    if (!openCycle || !cancelCyclePreview) return;
    setCancelCycleBusy(true);
    try {
      if (cancelCyclePreview.hasPreviousCycle) {
        await adapter.cancelForecastCycle(openCycle.id);
      } else {
        await adapter.deleteCycleNoRevert(openCycle.id);
      }
      const list = await adapter.listForecastCycles();
      setCycles(list);
      setCancelCycleFormOpen(false);
      setCancelCyclePreview(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setCancelCycleBusy(false);
    }
  };

  // Cycle automation (read-only here — drives the "Next cycle opens …" line).
  const [cycleAutomation, setCycleAutomation] = useState<Awaited<
    ReturnType<typeof adapter.getCycleAutomation>
  > | null>(null);
  useEffect(() => {
    let cancelled = false;
    adapter
      .getCycleAutomation()
      .then((v) => {
        if (!cancelled) setCycleAutomation(v);
      })
      .catch(() => {
        // Non-blocking — banner just hides the next-tick hint on failure.
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  // Per-rep Submit / Reopen review affordance. Only meaningful when
  // the currently-selected submission is a cycle-anchored draft.
  const [reviewBusy, setReviewBusy] = useState(false);
  const handleSubmitReview = async () => {
    if (!currentSubmission) return;
    setReviewBusy(true);
    try {
      await adapter.submitReview(currentSubmission.id);
      if (forecast) {
        const fresh = await adapter.listForecastSubmissionsForForecast(
          forecast.id,
        );
        setSubmissions(fresh);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setReviewBusy(false);
    }
  };
  const handleReopenReview = async () => {
    if (!currentSubmission) return;
    setReviewBusy(true);
    try {
      await adapter.reopenMyReview(currentSubmission.id);
      if (forecast) {
        const fresh = await adapter.listForecastSubmissionsForForecast(
          forecast.id,
        );
        setSubmissions(fresh);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <WorkspaceNav active="plan" />
      <ForecastDisclaimerBanner />
      <PickerBar
        currency={currency}
        scenario={scenario}
        windowMode={windowMode}
        period={period}
        customFrom={customFrom}
        customTo={customTo}
        targetPlanId={targetPlanId}
        targetPlans={targetPlans}
        onCurrency={setCurrency}
        onScenario={setScenario}
        onWindow={setWindowMode}
        onPeriod={setPeriod}
        onCustomFrom={setCustomFrom}
        onCustomTo={setCustomTo}
        onTargetPlan={setTargetPlanId}
      />

      {/* Cycle status row + Manage menu — sits in the picker chrome,
          not as a banner. Always reflects the open cycle (historical
          replay lives at /plan/cycles/:cycleId/review). */}
      <CycleStatusRow
        openCycle={openCycle}
        isManager={isManager}
        automationNext={cycleAutomation?.nextScheduledAt ?? null}
        onOpenNew={() => setOpenCycleFormOpen(true)}
        onClose={() => setCloseCycleFormOpen(true)}
        onEdit={openEditCycleForm}
        onCancel={openCancelCycleForm}
      />

      {/* Rep personal status row — only when viewer IS the viewed rep
          and they have a cycle-anchored draft. */}
      {viewerIsViewedRep &&
        currentSubmission?.cycleId &&
        currentSubmission.status === "draft" && (
          <RepStatusRow
            submission={{
              id: currentSubmission.id,
              submittedAt: currentSubmission.submittedAt ?? null,
            }}
            busy={reviewBusy}
            onSubmit={handleSubmitReview}
            onReopen={handleReopenReview}
          />
        )}

      {openCycleFormOpen && (
        <Card className="p-3 space-y-2 border-violet-300 bg-violet-50/50 text-sm">
          <div className="text-violet-900 font-medium">
            Open new forecast review cycle
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="block text-xs font-medium text-violet-900 mb-1">
                As-of date
              </span>
              <Input
                type="date"
                value={openCycleAsOf}
                onChange={(e) => setOpenCycleAsOf(e.target.value)}
                className="bg-white"
                disabled={openCycleBusy}
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-violet-900 mb-1">
                Due date (optional)
              </span>
              <Input
                type="date"
                value={openCycleDueDate}
                onChange={(e) => setOpenCycleDueDate(e.target.value)}
                className="bg-white"
                disabled={openCycleBusy}
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-violet-900 mb-1">
                Label (optional)
              </span>
              <Input
                value={openCycleLabel}
                onChange={(e) => setOpenCycleLabel(e.target.value)}
                placeholder="e.g. FY26 May board"
                className="bg-white"
                disabled={openCycleBusy}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpenCycleFormOpen(false)}
              disabled={openCycleBusy}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleOpenCycle}
              disabled={openCycleBusy || !openCycleAsOf}
            >
              {openCycleBusy ? "Opening…" : "Open cycle"}
            </Button>
          </div>
        </Card>
      )}

      {editCycleFormOpen && openCycle && (
        <Card className="p-3 space-y-2 border-violet-300 bg-violet-50/50 text-sm">
          <div className="text-violet-900 font-medium">Edit cycle metadata</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="block text-xs font-medium text-violet-900 mb-1">
                Label
              </span>
              <Input
                value={editCycleLabel}
                onChange={(e) => setEditCycleLabel(e.target.value)}
                placeholder="e.g. Feb 2026 forecast cycle"
                className="bg-white"
                disabled={editCycleBusy}
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-violet-900 mb-1">
                Due date
              </span>
              <Input
                type="date"
                value={editCycleDueDate}
                onChange={(e) => setEditCycleDueDate(e.target.value)}
                className="bg-white"
                disabled={editCycleBusy}
              />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-violet-900 mb-1">
              Notes
            </span>
            <Input
              value={editCycleNotes}
              onChange={(e) => setEditCycleNotes(e.target.value)}
              placeholder="e.g. Mid-month checkpoint before the board"
              maxLength={2000}
              className="bg-white"
              disabled={editCycleBusy}
            />
          </label>
          <div className="text-xs text-violet-700/80">
            To change the as-of date or anchor moment, use{" "}
            <strong>Cancel cycle</strong> and then open a new one with different
            settings.
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditCycleFormOpen(false)}
              disabled={editCycleBusy}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleEditCycle}
              disabled={editCycleBusy}
            >
              {editCycleBusy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </Card>
      )}

      {cancelCycleFormOpen && openCycle && cancelCyclePreview && (
        <Card className="p-3 space-y-2 border-amber-300 bg-amber-50/50 text-sm">
          {cancelCyclePreview.hasPreviousCycle ? (
            <>
              <div className="text-amber-900 font-medium">
                Cancel this cycle and reopen{" "}
                {cancelCyclePreview.previousCycleLabel || "the previous cycle"}?
              </div>
              <div className="text-amber-900 text-xs">
                The publication created when{" "}
                {cancelCyclePreview.previousCycleLabel || "the previous cycle"}{" "}
                was closed will be undone — it will be as if it never happened.
                Edits made under this cycle will move onto the reopened cycle's
                drafts.
              </div>
              {cancelCyclePreview.repsWithEdits.length > 0 ? (
                <div className="text-xs text-amber-900">
                  <div className="font-medium">Reps with edits:</div>
                  <ul className="list-disc list-inside">
                    {cancelCyclePreview.repsWithEdits.map((r) => (
                      <li key={r.userId}>
                        {r.userId} ({r.editedCellCount}{" "}
                        {r.editedCellCount === 1 ? "cell" : "cells"})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-xs text-amber-900/80 italic">
                  No reps have edits in this cycle.
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-amber-900 font-medium">
                Discard this cycle?
              </div>
              <div className="text-amber-900 text-xs">
                There is no earlier cycle to revert to. All edits made in this
                cycle will be permanently discarded.
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCancelCycleFormOpen(false);
                setCancelCyclePreview(null);
              }}
              disabled={cancelCycleBusy}
            >
              Keep cycle open
            </Button>
            <Button
              size="sm"
              onClick={handleCancelCycle}
              disabled={cancelCycleBusy}
            >
              {cancelCycleBusy
                ? "Cancelling…"
                : cancelCyclePreview.hasPreviousCycle
                  ? "Cancel cycle"
                  : "Discard cycle"}
            </Button>
          </div>
        </Card>
      )}

      {closeCycleFormOpen && openCycle && (
        <Card className="p-3 space-y-2 border-violet-300 bg-violet-50/50 text-sm">
          <div className="text-violet-900">
            Closing this cycle will publish all current drafts as the cycle's
            agreed snapshot. The next cycle opens automatically on the next
            scheduled tick — use <strong>Open new cycle</strong> after closing
            if you need one sooner.
          </div>
          <label
            htmlFor="close-cycle-notes"
            className="block text-xs font-medium text-violet-900 pt-1"
          >
            Close notes (optional)
          </label>
          <Input
            id="close-cycle-notes"
            value={closeCycleNotes}
            onChange={(e) => setCloseCycleNotes(e.target.value)}
            placeholder="e.g. May board-approved snapshot"
            className="bg-white"
            disabled={closeCycleBusy}
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCloseCycleFormOpen(false)}
              disabled={closeCycleBusy}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCloseCycle}
              disabled={closeCycleBusy}
            >
              {closeCycleBusy ? "Closing…" : "Close cycle"}
            </Button>
          </div>
        </Card>
      )}

      {/* Green submit-review banner removed in the chrome refactor —
          rep CTA now lives in the RepStatusRow up by the PickerBar. */}

      {loadError && (
        <Card className="p-4 border-red-200 bg-red-50/50 text-sm text-red-900">
          Failed to load plan data: {loadError}
        </Card>
      )}

      {!rep && planGrid != null && (
        <CompanyLevel
          reps={liveReps}
          companyFunnel={quoteFunnel?.company}
          monthLabels={planGrid.month_labels}
          companyFyTarget={planGrid.company.target}
          companyFyAdjusted={planGrid.company.adjusted_forecast}
          companyFyOutlook={planGrid.company.full_year_outlook}
          companyFyActualsYtd={planGrid.company.actuals_ytd}
          companyBaselineByMonth={
            planGrid.company.baseline_by_month ?? Array(12).fill(0)
          }
          companyAdjustmentByMonth={
            planGrid.company.adjustment_by_month ?? Array(12).fill(0)
          }
          companyPipelineByMonth={
            planGrid.company.pipeline_by_month ?? Array(12).fill(0)
          }
          companyBookedPipelineByMonth={
            planGrid.company.booked_pipeline_by_month ?? Array(12).fill(0)
          }
          companyActualsByMonth={
            planGrid.company.actuals_by_month ?? Array(12).fill(0)
          }
          companyPriorYearByMonth={
            planGrid.company.prior_year_by_month ?? Array(12).fill(0)
          }
        />
      )}
      {!rep && planGrid == null && !loadError && (
        <Card className="p-6 text-sm text-muted-foreground">
          Loading plan grid…
        </Card>
      )}
      {rep && !bucket && (
        <RepLevel
          rep={rep}
          repFunnel={quoteFunnel?.byRepId[rep.user_id]}
          monthLabels={planGrid?.month_labels ?? []}
          submissionId={submissionId}
          adjustments={adjustments}
          drift={drift}
          onAdjustmentsChange={reloadAdjustments}
        />
      )}
      {/* Rep-scoped material movers — surfaces plan-moving baseline shifts in
          the rep's own buckets. Scoped to the active forecast stream (which
          tracks the drilled-in rep). Reuses the calibration list endpoint. */}
      {rep && !bucket && forecast && (
        <MoversToReviewPanel forecastId={forecast.id} repId={rep.user_id} />
      )}
      {rep && bucket && (
        <BucketLevel
          rep={rep}
          bucket={bucket}
          bucketFunnel={quoteFunnel?.byBucketId[bucket.forecast_bucket_id]}
          monthLabels={planGrid?.month_labels ?? []}
          submissionId={submissionId}
          adjustments={adjustments}
          onAdjustmentsChange={reloadAdjustments}
        />
      )}
      {repId && !rep && planGrid != null && (
        <Card className="p-6 text-sm text-muted-foreground">
          Rep not found in this plan.
        </Card>
      )}
      {rep && bucketId && !bucket && (
        <Card className="p-6 text-sm text-muted-foreground">
          Bucket not found.
        </Card>
      )}
      {/* forecast is currently unused at company level but will drive the
          plan-grid endpoint above once it exists. */}
      {forecast == null && submissions.length === 0 && !loadError && (
        <p className="sr-only">Loading forecast...</p>
      )}
    </div>
  );
}
